import { daysOfTheWeekS, notificationSettingTypes } from '@monorepo/helpers';
import { FC, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { typeNotificationSetting, typeNotificationSettingType, typeNotificationType, typeUser } from '@monorepo/models';
import { useGetIsDarkMode } from 'src/custom-hooks';
import Select from 'react-select';

type propsType = {
    addNotificationSetting?: (ns: typeNotificationSetting) => void;
    closeModal: () => void;
    deleteNotificationSetting?: (nsId: number) => void;
    editNotificationSetting?: (ns: typeNotificationSetting) => void;
    head1Id: number | null | undefined;
    head2Id: number | null | undefined;
    newNotificationSettings: typeNotificationSetting[];
    notificationSettingToEdit: typeNotificationSetting | null;
    type: typeNotificationType;
    updateHead1Id: (updateHead1Id: number | null | undefined) => void;
    updateHead2Id: (updateHead1Id: number | null | undefined) => void;
    users: typeUser[];
};

export const NotificationsSettingsCrudModal: FC<propsType> = ({
    addNotificationSetting, closeModal, deleteNotificationSetting,
    editNotificationSetting, head1Id, head2Id, newNotificationSettings,
    notificationSettingToEdit, type, updateHead1Id, updateHead2Id, users
}) => {
    const [notificationSetting, setNotificationSetting] = useState<typeNotificationSetting>(notificationSettingToEdit ?? {
        hour: 10,
        id: 0,
        type: 1,
        weekday: null
    });
    const isAvUshers = type === 'meetings-midweek-av-ushers' || type === 'meetings-public-av-ushers';
    const isDarkMode = useGetIsDarkMode();
    const isEdit = !!notificationSettingToEdit;

    const isOk: boolean = useMemo(() =>
        (notificationSetting.type > 4 && notificationSetting.weekday === null)
        || (notificationSetting.type < 5 && notificationSetting.weekday !== null)
    , [notificationSetting]);

    const isDuplicated: boolean = useMemo(() =>
        isOk
        && newNotificationSettings.filter(ns => ns.id !== notificationSetting.id).some(ns =>
            ns.type === notificationSetting.type
            && ns.weekday === notificationSetting.weekday
            && ns.hour === notificationSetting.hour
        )
    , [isOk, notificationSetting, newNotificationSettings]);

    const isValid: boolean = useMemo(() =>
        !isDuplicated && isOk
    , [isDuplicated, isOk]);

    const acceptHandler = () => {
        if (!isValid) {
            return;
        }
        addNotificationSetting!(notificationSetting);
        closeModal();
    }

    const deleteHandler = () => {
        deleteNotificationSetting!(notificationSetting.id);
        closeModal();
    }

    const editHandler = () => {
        if (!isValid) {
            return;
        }
        editNotificationSetting!(notificationSetting);
        closeModal();
    }

    return (
        <Modal show size={'xl'} onHide={closeModal}>
            <Modal.Header className={isDarkMode ? 'bg-dark text-white' : ''} closeButton>
                <h3 className={'mb-0'}>
                    {isEdit ? 'EDITAR' : 'AGREGAR'} NOTIFICACIÓN
                </h3>
            </Modal.Header>
            <Modal.Body className={`${isDarkMode ? 'bg-lightdark text-white' :''} px-4`}>
                <div className={'row'}>
                    <div className={'col-lg-5'}>
                        <h5>
                            Frecuencia
                        </h5>
                        <select className={'form-select mt-2'}
                            value={notificationSetting.type}
                            onChange={e => {
                                const type = parseInt(e.target.value) as typeNotificationSettingType;
                                setNotificationSetting(x => ({
                                    ...x,
                                    type,
                                    weekday: null
                                }));
                            }}
                        >
                            {notificationSettingTypes.map(nt =>
                                <option key={nt.type} value={nt.type}>
                                    {nt.label}
                                </option>
                            )}
                        </select>
                        {notificationSetting.type < 5 &&
                            <div className={'mt-3'}>
                                <select className={'form-select'}
                                    value={notificationSetting.weekday ?? ''}
                                    onChange={e => {
                                        const weekday = parseInt(e.target.value);
                                        setNotificationSetting(x => ({
                                            ...x,
                                            weekday
                                        }));
                                    }}
                                >
                                    <option value='' disabled>
                                        Seleccionar Día de la Semana
                                    </option>
                                    {[...daysOfTheWeekS.slice(1), daysOfTheWeekS[0]].map((d, i) =>
                                        <option key={i} value={i === 6 ? 0 : i + 1}>
                                            {d}
                                        </option>
                                    )}
                                </select>
                            </div>
                        }
                    </div>
                    <div className={'col-lg-3'}>
                        <h5>
                            Horario
                        </h5>
                        <div className={'d-flex justify-content-start mt-2'}>
                            <Select className={'text-dark me-2'}
                                options={Array.from({ length: 15 }, (_, i) => ({ hour: i + 8 }))}
                                getOptionLabel={h => h.hour.toString().padStart(2, '0')}
                                getOptionValue={h => h.hour.toString()}
                                value={{ hour: notificationSetting.hour }}
                                onChange={h => setNotificationSetting(x => ({ ...x, hour: h!.hour ?? 0 }))}
                            />
                            <Select className={'text-dark'}
                                options={[{ minutes: 0 }]}
                                    // , { minutes: 15 }, { minutes: 30 }, { minutes: 45 }, { minutes: 10 }, { minutes: 20 }, { minutes: 40 }, { minutes: 50 }
                                getOptionLabel={m => m.minutes.toString().padStart(2, '0')}
                                getOptionValue={m => m.minutes.toString()}
                                value={{ minutes: 0 }}
                                onChange={m => {}}
                            />
                        </div>
                    </div>
                    {(type !== 'meetings-midweek-cleaning' && type !== 'meetings-public-cleaning') &&
                        <div className={'col-lg-4'}>
                            <h5>
                                Responsables Reemplazos
                            </h5>
                            {isAvUshers &&
                                <h5 className={'mt-3'} style={{ fontSize: '1rem' }}>
                                    Audio y Video:
                                </h5>
                            }
                            <Select className={'text-dark mt-2'}
                                options={users}
                                getOptionLabel={u => `${u.name} ${u.lastName}`}
                                getOptionValue={u => u.id.toString()}
                                value={users.find(u => u.id === head1Id) ?? null}
                                onChange={u => {
                                    updateHead1Id(u?.id ?? null);
                                }}
                                isClearable
                            />
                            {(!!head2Id || isAvUshers) &&
                                <>
                                {isAvUshers &&
                                    <h5 className={'mt-3'} style={{ fontSize: '1rem' }}>
                                        Acomodadores:
                                    </h5>
                                }
                                <Select className={'text-dark mt-3'}
                                    options={users}
                                    getOptionLabel={u => `${u.name} ${u.lastName}`}
                                    getOptionValue={u => u.id.toString()}
                                    value={users.find(u => u.id === head2Id) ?? null}
                                    onChange={u => {
                                        updateHead2Id(u?.id ?? null);
                                    }}
                                    isClearable
                                />
                                </>
                            }
                        </div>
                    }
                </div>
                <p className={'px-2 mt-4 mb-0'}>
                    *Si se configura una notificación posterior a la asignación no se enviará. Ejemplo: reunión un jueves con notificación para el "viernes de la misma semana". Lo mismo para los horarios futuros, en el caso de asignaciones con horario (carritos y servicio del campo; reuniones no porque no se guardan sus horarios); no se enviarán.
                </p>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? 'bg-dark text-white' :''}>
                {isDuplicated &&
                    <div className={'alert alert-danger'} role='alert'>
                        Esta notificación está duplicada
                    </div>
                }
                <button className={`btn btn-general-blue ${isEdit ? 'col-lg-3 offset-lg-3' : 'col w-100'} fw-bold py-2`}
                    onClick={isEdit ? editHandler : acceptHandler}
                    disabled={!isValid}
                >
                    ACEPTAR
                </button>
                {isEdit &&
                    <button className={'btn btn-general-red col-lg-3 fw-bold py-2'}
                        onClick={deleteHandler}
                    >
                        ELIMINAR
                    </button>
                }
            </Modal.Footer>
        </Modal>
    );
}
